'use client'

import { Fragment, useState, type ReactNode } from 'react'
import { DM_Mono, DM_Sans, Poppins } from 'next/font/google'
import { SMART_AI_FLIP, SMART_AI_HERO, SMART_AI_WAVE } from '@/lib/smart-ai-data'

const dmSans = DM_Sans({
  subsets: ['latin'],
  weight: ['400', '500', '700'],
})

const dmMono = DM_Mono({
  subsets: ['latin'],
  weight: ['400', '500'],
  variable: '--ai-flip-mono',
})

const poppins = Poppins({
  subsets: ['latin'],
  weight: ['300', '400'],
})

type FlipState = 'before' | 'after'

const MODEL_LOGOS = [
  SMART_AI_HERO.models.find((m) => m.name === 'OpenAI')!,
  SMART_AI_HERO.models.find((m) => m.name === 'Claude')!,
  SMART_AI_HERO.models.find((m) => m.name === 'Gemini')!,
]

function Locks() {
  const shapes = ['dot', 'line', 'dot-lg', 'line', 'logos', 'line', 'dot-lg', 'line', 'dot'] as const
  return (
    <div className="ai-flip-locks">
      {shapes.map((shape, i) => (
        <Fragment key={i}>
          {shape === 'dot' && <div className="ai-flip-locks__dot" />}
          {shape === 'dot-lg' && <div className="ai-flip-locks__dot ai-flip-locks__dot--lg" />}
          {shape === 'line' && <div className="ai-flip-locks__line" />}
          {shape === 'logos' && (
            <div className="ai-flip-locks__center" aria-label="Claude, OpenAI, and Gemini">
              {MODEL_LOGOS.map((logo) => (
                // eslint-disable-next-line @next/next/no-img-element
                <img key={logo.name} src={logo.src} alt={logo.name} />
              ))}
            </div>
          )}
        </Fragment>
      ))}
    </div>
  )
}

function LeadRows({
  rows,
}: {
  rows: readonly { bar: string; pill: string; dead: boolean }[]
}) {
  return (
    <div className="ai-flip-rows">
      {rows.map((row) => (
        <div key={`${row.pill}-${row.bar}`} className={`ai-flip-lrow${row.dead ? ' dead' : ''}`}>
          <span className="ai-flip-dot" />
          <span className="ai-flip-bar" style={{ width: row.bar }} />
          <span className="ai-flip-pill">{row.pill}</span>
        </div>
      ))}
    </div>
  )
}

function Dial({ time, note, slow }: { time: string; note: string; slow?: boolean }) {
  return (
    <div className="ai-flip-dial">
      <div className={`ai-flip-ring${slow ? ' ai-flip-ring--slow' : ''}`}>
        <i>
          <b>{time}</b>
          <span>first reply</span>
        </i>
      </div>
      <span className="ai-flip-was">{note}</span>
    </div>
  )
}

function Timeline({ activeDays, booked }: { activeDays: number; booked?: boolean }) {
  return (
    <div className="ai-flip-tline">
      {SMART_AI_FLIP.follow.days.map((day, i) => {
        const on = i < activeDays
        const isLast = i === SMART_AI_FLIP.follow.days.length - 1
        return (
          <div key={day} className={`ai-flip-nd${on ? ' on' : ' idle'}`}>
            <span className="ai-flip-d">{day}</span>
            {booked && isLast && on ? <span className="ai-flip-win">Booked</span> : <span className="ai-flip-tb" />}
          </div>
        )
      })}
    </div>
  )
}

function FlipCta() {
  return (
    <a
      className="ai-flip-cta-wrap"
      href={SMART_AI_WAVE.simpleAgenticHref}
      target="_blank"
      rel="noopener noreferrer"
    >
      <span className="ai-flip-cta-glow" aria-hidden="true" />
      <span className="ai-flip-cta">
        {SMART_AI_WAVE.simpleAgenticCta}
        <span aria-hidden="true">→</span>
      </span>
    </a>
  )
}

function FlipCard({
  n,
  title,
  before,
  after,
  visualBefore,
  visualAfter,
  stateLabel,
}: {
  n: string
  title: string
  before: string
  after: string
  visualBefore: ReactNode
  visualAfter: ReactNode
  stateLabel: string
}) {
  return (
    <article className="ai-flip-card">
      <div className="ai-flip-stack">
        <div className="ai-flip-vis b">{visualBefore}</div>
        <div className="ai-flip-vis a">{visualAfter}</div>
      </div>
      <div>
        <p className="ai-flip-num">{n}</p>
        <h3 className="ai-flip-ttl">{title}</h3>
      </div>
      <div className="ai-flip-cap">
        <p className="pb">{before}</p>
        <p className="pa">{after}</p>
      </div>
      <div className="ai-flip-foot">
        <span className="ai-flip-state">
          <i />
          <span>{stateLabel}</span>
        </span>
        <FlipCta />
      </div>
    </article>
  )
}

export default function SmartAIWidgets() {
  const [state, setState] = useState<FlipState>('before')
  const { capture, respond, follow } = SMART_AI_FLIP
  const stateLabel = state === 'after' ? 'With the AI running' : 'Current state'

  return (
    <section
      className={`ai-flip ${dmSans.className} ${dmMono.variable}`}
      id="stages"
      aria-label="SmartAgents stages"
      data-state={state}
    >
      <div className="ai-flip-top">
        <div className={`ai-flip-hero ${poppins.className}`}>
          <h1>{SMART_AI_WAVE.title}</h1>
          <p className="ai-flip-lead">{SMART_AI_WAVE.sub}</p>
          <a
            className="ai-flip-cta-wrap ai-flip-hero-cta"
            href={SMART_AI_WAVE.simpleAgenticHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="ai-flip-cta-glow" aria-hidden="true" />
            <span className="ai-flip-cta">
              {SMART_AI_WAVE.simpleAgenticCta}
              <span aria-hidden="true">→</span>
            </span>
          </a>
          <Locks />
        </div>

        <div className="ai-flip-wrap">
        <div className="ai-flip-togwrap">
          <div className="ai-flip-seg" role="group" aria-label="State">
            <button
              type="button"
              aria-pressed={state === 'before'}
              onClick={() => setState('before')}
            >
              Before
            </button>
            <button
              type="button"
              aria-pressed={state === 'after'}
              onClick={() => setState('after')}
            >
              After
            </button>
          </div>
        </div>

        <div className="ai-flip-grid">
          <FlipCard
            n={capture.n}
            title={capture.title}
            before={capture.before}
            after={capture.after}
            visualBefore={<LeadRows rows={capture.beforeRows} />}
            visualAfter={<LeadRows rows={capture.afterRows} />}
            stateLabel={stateLabel}
          />
          <FlipCard
            n={respond.n}
            title={respond.title}
            before={respond.before}
            after={respond.after}
            visualBefore={<Dial time={respond.beforeTime} note={respond.beforeNote} slow />}
            visualAfter={<Dial time={respond.afterTime} note={respond.afterNote} />}
            stateLabel={stateLabel}
          />
          <FlipCard
            n={follow.n}
            title={follow.title}
            before={follow.before}
            after={follow.after}
            visualBefore={<Timeline activeDays={1} />}
            visualAfter={<Timeline activeDays={4} booked />}
            stateLabel={stateLabel}
          />
        </div>
        </div>
      </div>
    </section>
  )
}
